import { useState } from 'react';

import { BsBuilding, BsPerson } from 'react-icons/bs';

import SearchOverlay, { highlightMatch } from '#globalComponents/SearchOverlay/SearchOverlay';
import { useDebouncedValue } from '#globalHooks/useDebouncedValue';
import { getErrorMessage } from '#lib/errors/errorMessages';

import { useLeadData } from '../../contexts/LeadDataContext';
import { useCompanyPosition } from '../../models/leadSearch/mutations';
import { useLeadSearch } from '../../models/leadSearch/queries';

const getItemKey = (item) =>
	item.type === 'contact' ? `contact-${item.queryId}-${item.contactId}` : `company-${item.queryId}-${item.companyId}`;

const LeadSearchOverlay = () => {
	const { navigateToCompany, orderBy, orderDir, pageSize } = useLeadData();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [errorMessage, setErrorMessage] = useState(null);

	const debouncedQuery = useDebouncedValue(query.trim());
	const { data, isFetching } = useLeadSearch(open ? debouncedQuery : '');
	const positionMutation = useCompanyPosition();
	const [pendingItemKey, setPendingItemKey] = useState(null);

	const groups = [
		{ key: 'companies', label: 'Firmen', items: data?.companies ?? [] },
		{ key: 'contacts', label: 'Kontakte', items: data?.contacts ?? [] },
	];

	const handleSelect = async (item) => {
		setErrorMessage(null);
		setPendingItemKey(getItemKey(item));
		try {
			const { page } = await positionMutation.mutateAsync({
				queryId: item.queryId,
				companyId: item.companyId,
				orderBy,
				orderDir,
				pageSize,
			});
			navigateToCompany({ queryId: item.queryId, page, highlightId: item.companyId });
			setQuery('');
			setOpen(false);
		} catch (error) {
			setErrorMessage(getErrorMessage(error));
		} finally {
			setPendingItemKey(null);
		}
	};

	const handleOpenChange = (nextOpen) => {
		setOpen(nextOpen);
		if (!nextOpen) setErrorMessage(null);
	};

	const renderItem = (item, { isPending }) => {
		const Icon = item.type === 'contact' ? BsPerson : BsBuilding;
		const title = item.type === 'contact' ? item.contactName || '—' : item.companyName || '—';
		const subtitleParts =
			item.type === 'contact'
				? [item.companyName, item.companyRole].filter(Boolean)
				: [item.domain].filter(Boolean);
		const showSnippet = item.matchedValue && item.matchedValue !== title;
		return (
			<>
				<Icon className="w-4 h-4 text-bgBase-800 flex-shrink-0 mt-0.5" />
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-text truncate">{highlightMatch(title, debouncedQuery)}</p>
					{subtitleParts.length > 0 && (
						<p className="text-[11px] text-bgBase-800 truncate mt-0.5">{subtitleParts.join(' · ')}</p>
					)}
					{showSnippet && (
						<p className="text-[11px] text-bgBase-800 truncate mt-0.5">
							{highlightMatch(item.matchedValue, debouncedQuery)}
						</p>
					)}
				</div>
				<span className="text-[10px] text-primary-700 bg-purple-50 rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5 max-w-[10rem] truncate">
					{item.queryName}
				</span>
				{isPending && (
					<div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin flex-shrink-0 mt-0.5" />
				)}
			</>
		);
	};

	return (
		<SearchOverlay
			open={open}
			onOpenChange={handleOpenChange}
			placeholder="Firmen & Kontakte suchen …"
			query={query}
			onQueryChange={setQuery}
			groups={groups}
			renderItem={renderItem}
			getItemKey={getItemKey}
			onSelect={handleSelect}
			isLoading={isFetching}
			pendingItemKey={pendingItemKey}
			errorMessage={errorMessage}
		/>
	);
};

export default LeadSearchOverlay;
